import { Button, Drawer, Layout } from "antd";
import { ReactNode, Suspense, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../app/store";
import AlertIcon from "../assets/icons/AlertIcon";
import SidebarContent from "../components/common/sidebar/SidebarContent";
import SidebarItems from "../components/common/sidebar/SidebarItems";
import Loader from "../components/ui/Loader";
import { useSendVerificationEmailMutation } from "../features/auth/authApi";
import { useGetEmployeeDetailsQuery } from "../features/employee/employeeApi";
import useError from "../hooks/useError";
import useSidebar from "../hooks/useSidebar";
import { toast } from "../lib/toast";
import AppLayout from "./AppLayout";

export interface SidebarLayoutProps {
  children?: ReactNode;
  appTitle?: string;
  title?: string;
  contentClass?: string;
  tabList?: {
    label: string;
    link: string;
    redirect?: string;
    disabled?: boolean;
  }[];
}

export default function SidebarLayout({
  children,
  title,
  appTitle,
  tabList,
  contentClass,
}: SidebarLayoutProps) {
  const {
    collapsed,
    openSidebar,
    openMenuKeys,
    handleOpenMenu,
    handleCollapsed,
    handleShowSidebar,
    handleCloseSidebar,
  } = useSidebar();
  const { authInfo } = useAppSelector((state) => state.auth);
  const { data: userData, isLoading: userLoading } = useGetEmployeeDetailsQuery(
    authInfo?.id?.toString() || "",
    {
      skip: !authInfo?.id,
    }
  );
  const navigate = useNavigate();
  const [sendVerificationEmail, { isSuccess, isError, error, isLoading }] =
    useSendVerificationEmailMutation();
  useError(isError, error, "Email send fail, please try again");

  const handleVerify = () => {
    sendVerificationEmail({
      email: authInfo?.email,
    });
  };

  useEffect(() => {
    if (isSuccess) {
      toast("success", "Email send successfully, please check your email");
      navigate("/verify-account");
    }
  }, [isSuccess, navigate]);

  const isEmailVerified =
    userData?.data?.employee?.member?.profile?.email_verified;

  return (
    <div>
      {!isEmailVerified ? (
        <div className="h-12 bg-[#f6ddbc] flex justify-center items-center text-center text-sm font-medium !text-[#fa6400] sticky z-[999] w-full top-0">
          <AlertIcon />
          <span className="px-2">Your email address isn’t verified yet.</span>
          <Button
            type="primary"
            className="!h-7 !text-xs"
            size="small"
            onClick={handleVerify}
            loading={isLoading}
          >
            Verify Now
          </Button>
        </div>
      ) : null}
      <Layout>
        <div className="lg:hidden ">
          <Drawer
            placement="left"
            closable={false}
            onClose={handleCloseSidebar}
            open={openSidebar}
            key="left"
            contentWrapperStyle={{ width: "auto" }}
            bodyStyle={{ padding: 0, overflowY: "hidden" }}
            // styles={{
            //   body: { padding: 0, overflowY: "hidden" },
            //   wrapper: { width: "auto" },
            // }}
          >
            <SidebarItems
              collapsed={false}
              openMenuKeys={openMenuKeys}
              handleOpenMenu={handleOpenMenu}
              data={userData}
              isMobile
            />
          </Drawer>
        </div>
        <div className="hidden lg:block border-r border-extra-light">
          <SidebarItems
            collapsed={collapsed}
            openMenuKeys={openMenuKeys}
            handleOpenMenu={handleOpenMenu}
            data={userData}
          />
        </div>

        {/* Sidebar content is main children component, where show children data. This component contains Header items like: Profile and Notification  */}
        <AppLayout
          title={appTitle || title}
          className={`w-full ${
            collapsed ? "lg:w-[calc(100vw-80px)]" : "lg:w-[calc(100vw-321px)]"
          }`}
        >
          <SidebarContent
            collapsed={collapsed}
            handleCollapsed={handleCollapsed}
            handleShowSidebar={handleShowSidebar}
            title={title}
            tabList={tabList}
            data={userData}
            isLoading={userLoading}
            contentClass={contentClass}
          >
            <Suspense fallback={<Loader />}>{children}</Suspense>
          </SidebarContent>
        </AppLayout>
      </Layout>
    </div>
  );
}
